import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, isTimeoutError, isNetworkError } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';
import { Save, RotateCcw, Loader2, ArrowLeft, X, Plus, AlertTriangle, RefreshCw, Upload, Trash2, Camera } from 'lucide-react';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Common medical specialties
const SPECIALTY_OPTIONS = [
  'General Practice',
  'Cardiology',
  'Dentistry',
  'Dermatology',
  'Endocrinology',
  'Gastroenterology',
  'Neurology',
  'Oncology',
  'Ophthalmology',
  'Orthodontics',
  'Orthopedics',
  'Pediatrics',
  'Psychiatry',
  'Radiology',
  'Surgery',
  'Urology',
  'Family Medicine',
  'Internal Medicine',
  'Emergency Medicine',
  'Anesthesiology',
  'Pathology',
  'Physical Therapy'
];

const DoctorProfileEdit = () => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Original values (for reset functionality)
  const [originalData, setOriginalData] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    bio: '',
    specialties: [],
    qualifications: '',
    photoUrl: ''
  });

  // Track if form has unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // New specialty input state
  const [newSpecialty, setNewSpecialty] = useState('');
  const [showSpecialtyInput, setShowSpecialtyInput] = useState(false);

  // Doctor ID state
  const [doctorId, setDoctorId] = useState(null);

  // Track profile version for optimistic concurrency control
  const [profileUpdatedAt, setProfileUpdatedAt] = useState(null);

  // Conflict dialog state
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  // Use the unsaved changes hook for navigation blocking
  const {
    isBlocked: showNavigationWarning,
    confirmNavigation,
    cancelNavigation,
    checkNavigation
  } = useUnsavedChanges(hasChanges);

  // Handle navigation with unsaved changes check (for programmatic navigation)
  const handleNavigate = useCallback((path) => {
    if (!checkNavigation(path)) {
      navigate(path);
    }
  }, [checkNavigation, navigate]);

  // Fetch doctor profile data
  const fetchProfile = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setApiError(null);

    try {
      // First get the doctor ID from /auth/me
      const meResponse = await fetchWithTimeout(
        `${API_URL}/auth/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!meResponse.ok) {
        throw new Error('Failed to get user information');
      }

      const meData = await meResponse.json();
      const fetchedDoctorId = meData.doctorId;

      if (!fetchedDoctorId) {
        throw new Error('Doctor profile not found');
      }

      setDoctorId(fetchedDoctorId);

      // Now fetch the full doctor profile
      const profileResponse = await fetchWithTimeout(
        `${API_URL}/doctors/${fetchedDoctorId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!profileResponse.ok) {
        throw new Error('Failed to fetch doctor profile');
      }

      const profileData = await profileResponse.json();

      // Parse specialties - handle string, array, null, or undefined
      let specialtiesArray = [];
      if (Array.isArray(profileData.specialties)) {
        specialtiesArray = profileData.specialties;
      } else if (typeof profileData.specialties === 'string') {
        try {
          const parsed = JSON.parse(profileData.specialties);
          specialtiesArray = Array.isArray(parsed) ? parsed : [];
        } catch {
          specialtiesArray = [];
        }
      }

      // Store original data for reset functionality
      const original = {
        bio: profileData.bio || '',
        specialties: specialtiesArray,
        qualifications: profileData.qualifications || '',
        photoUrl: profileData.photoUrl || ''
      };

      setOriginalData(original);
      setFormData(original);
      setHasChanges(false);

      // Store the updated_at timestamp for optimistic concurrency control
      setProfileUpdatedAt(profileData.updatedAt);

    } catch (error) {
      console.error('Error fetching profile:', error);
      setApiError(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch profile on mount
  useEffect(() => {
    if (isAuthenticated && user?.role === 'doctor') {
      fetchProfile();
    }
  }, [isAuthenticated, user, fetchProfile]);

  // Redirect if not authenticated or not a doctor
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Check for changes whenever form data changes
  // Note: photoUrl is not included because photo uploads are saved immediately
  useEffect(() => {
    if (originalData) {
      const changed =
        formData.bio !== originalData.bio ||
        formData.qualifications !== originalData.qualifications ||
        JSON.stringify(formData.specialties) !== JSON.stringify(originalData.specialties);
      setHasChanges(changed);
    }
  }, [formData, originalData]);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add a specialty
  const handleAddSpecialty = (specialty) => {
    const currentSpecialties = Array.isArray(formData.specialties) ? formData.specialties : [];
    if (specialty && !currentSpecialties.includes(specialty)) {
      setFormData(prev => ({
        ...prev,
        specialties: [...(Array.isArray(prev.specialties) ? prev.specialties : []), specialty]
      }));
    }
    setNewSpecialty('');
    setShowSpecialtyInput(false);
  };

  // Remove a specialty
  const handleRemoveSpecialty = (specialtyToRemove) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialtyToRemove)
    }));
  };

  // Reset form to original values
  const handleReset = () => {
    if (originalData) {
      setFormData({
        bio: originalData.bio,
        specialties: [...originalData.specialties],
        qualifications: originalData.qualifications,
        photoUrl: originalData.photoUrl
      });
      setHasChanges(false);
    }
  };

  // Save profile changes
  const handleSave = async () => {
    if (!doctorId) return;

    setSaving(true);
    setApiError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/doctors/${doctorId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            bio: formData.bio,
            specialties: formData.specialties,
            qualifications: formData.qualifications,
            photoUrl: formData.photoUrl,
            expectedUpdatedAt: profileUpdatedAt // For optimistic concurrency control
          })
        },
        15000
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle concurrent edit conflict
        if (response.status === 409 && errorData.code === 'CONCURRENT_EDIT_CONFLICT') {
          setConflictData({
            message: errorData.message,
            currentUpdatedAt: errorData.currentUpdatedAt
          });
          setShowConflictDialog(true);
          return;
        }

        throw new Error(errorData.error || 'Failed to update profile');
      }

      const responseData = await response.json();

      // Update original data to match saved data
      setOriginalData({ ...formData });
      setHasChanges(false);

      // Update the profileUpdatedAt with the new timestamp from the server
      if (responseData.updatedAt) {
        setProfileUpdatedAt(responseData.updatedAt);
      }

      // Show success toast notification
      toast.success('Profile saved successfully!', {
        id: 'profile-save-success',
      });

    } catch (error) {
      console.error('Error saving profile:', error);
      setApiError(error);
    } finally {
      setSaving(false);
    }
  };

  // Handle conflict resolution: refresh and discard local changes
  const handleRefreshAfterConflict = async () => {
    setShowConflictDialog(false);
    setConflictData(null);
    setHasChanges(false);
    await fetchProfile();
    toast.success('Profile refreshed with latest changes', {
      id: 'profile-refresh-success',
    });
  };

  // Handle conflict resolution: force save (overwrites other changes)
  const handleForceSave = async () => {
    if (!doctorId) return;

    setShowConflictDialog(false);
    setSaving(true);
    setApiError(null);

    try {
      // Save without expectedUpdatedAt to force the update (last write wins)
      const response = await fetchWithTimeout(
        `${API_URL}/doctors/${doctorId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            bio: formData.bio,
            specialties: formData.specialties,
            qualifications: formData.qualifications,
            photoUrl: formData.photoUrl
            // No expectedUpdatedAt = force save
          })
        },
        15000
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update profile');
      }

      const responseData = await response.json();

      // Update original data to match saved data
      setOriginalData({ ...formData });
      setHasChanges(false);
      setConflictData(null);

      // Update the profileUpdatedAt with the new timestamp from the server
      if (responseData.updatedAt) {
        setProfileUpdatedAt(responseData.updatedAt);
      }

      // Show success toast notification
      toast.success('Profile saved successfully (overwriting other changes)', {
        id: 'profile-force-save-success',
      });

    } catch (error) {
      console.error('Error force saving profile:', error);
      setApiError(error);
    } finally {
      setSaving(false);
    }
  };

  // Handle retry
  const handleRetry = useCallback(() => {
    setApiError(null);
    fetchProfile();
  }, [fetchProfile]);

  // Handle photo file selection
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select an image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload the file
    handlePhotoUpload(file);
  };

  // Upload photo to server
  const handlePhotoUpload = async (file) => {
    if (!doctorId || !token) return;

    setUploadingPhoto(true);
    setApiError(null);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`${API_URL}/doctors/${doctorId}/photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload photo');
      }

      const data = await response.json();

      // Update form data with new photo URL
      setFormData(prev => ({ ...prev, photoUrl: data.photoUrl }));
      setOriginalData(prev => ({ ...prev, photoUrl: data.photoUrl }));
      setPhotoPreview(null);

      toast.success('Photo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      setPhotoPreview(null);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Delete current photo
  const handleDeletePhoto = async () => {
    if (!doctorId || !token || !formData.photoUrl) return;

    setDeletingPhoto(true);
    setApiError(null);

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/photo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete photo');
      }

      // Clear photo URL
      setFormData(prev => ({ ...prev, photoUrl: '' }));
      setOriginalData(prev => ({ ...prev, photoUrl: '' }));
      setPhotoPreview(null);

      toast.success('Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error(error.message || 'Failed to delete photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  // Get the full photo URL (handle relative paths)
  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // For relative paths like /uploads/xxx, prepend the API base URL
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}${url}`;
  };

  // Show loading during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      {/* Unsaved Changes Warning Dialog */}
      {showNavigationWarning && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="unsaved-changes-dialog">
          <div className="bg-white dark:bg-obsidian border border-light-border dark:border-border rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-display text-xl italic text-light-text dark:text-pure-white">Unsaved Changes</h3>
                <p className="text-light-text-secondary dark:text-silver/60 text-sm mt-1">You have unsaved changes that will be lost.</p>
              </div>
            </div>
            <p className="text-slate-600 dark:text-silver/80 mb-6">
              Are you sure you want to leave this page? Your changes will not be saved.
            </p>
            <div className="flex gap-4">
              <button
                onClick={cancelNavigation}
                className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-xl text-slate-600 dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth font-mono text-sm uppercase tracking-widest"
                data-testid="stay-button"
              >
                Stay
              </button>
              <button
                onClick={confirmNavigation}
                className="flex-1 py-3 px-6 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 hover:bg-red-500/30 transition-smooth font-mono text-sm uppercase tracking-widest"
                data-testid="leave-button"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Concurrent Edit Conflict Warning Dialog */}
      {showConflictDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="conflict-dialog">
          <div className="bg-white dark:bg-obsidian border border-light-border dark:border-border rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h3 className="font-display text-xl italic text-light-text dark:text-pure-white">Profile Modified</h3>
                <p className="text-light-text-secondary dark:text-silver/60 text-sm mt-1">Another session has edited this profile.</p>
              </div>
            </div>
            <p className="text-slate-600 dark:text-silver/80 mb-4">
              {conflictData?.message || 'This profile was modified in another browser tab or by another user while you were editing.'}
            </p>
            <p className="text-light-text-secondary dark:text-silver/60 text-sm mb-6">
              You can either refresh to see the latest changes (discarding your edits), or force save to overwrite the other changes with yours.
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleRefreshAfterConflict}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-6 border border-light-border dark:border-border rounded-xl text-slate-600 dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth font-mono text-sm uppercase tracking-widest"
                data-testid="refresh-button"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={handleForceSave}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-orange-500/20 border border-orange-500/30 rounded-xl text-orange-400 hover:bg-orange-500/30 transition-smooth font-mono text-sm uppercase tracking-widest"
                data-testid="force-save-button"
              >
                <Save className="w-4 h-4" />
                Force Save
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => handleNavigate('/doctor/dashboard')}
            className="flex items-center gap-2 text-light-text-secondary dark:text-silver/60 hover:text-slate-700 dark:hover:text-silver mb-4 transition-smooth"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Edit Profile
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Update your professional profile information
          </p>
        </div>

        {/* API Error Alert */}
        {apiError && (
          <ApiErrorAlert
            error={apiError}
            onRetry={handleRetry}
            isRetrying={loading}
          />
        )}

        {/* Loading State */}
        {loading ? (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-8">
            <div className="animate-pulse space-y-6">
              <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-1/4"></div>
              <div className="h-32 bg-slate-200 dark:bg-silver/20 rounded"></div>
              <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-1/4"></div>
              <div className="h-24 bg-slate-200 dark:bg-silver/20 rounded"></div>
            </div>
          </div>
        ) : (
          <>
            {/* Profile Form */}
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
              {/* Bio */}
              <div className="mb-6">
                <label htmlFor="profile-bio" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                  Bio / Description
                </label>
                <textarea
                  id="profile-bio"
                  value={formData.bio}
                  onChange={(e) => handleFieldChange('bio', e.target.value)}
                  placeholder="Tell patients about yourself, your experience, and your approach to care..."
                  className="w-full px-4 py-3 bg-white dark:bg-obsidian border border-light-border dark:border-border rounded-xl font-body text-light-text dark:text-silver focus:outline-none focus:border-slate-400 dark:focus:border-white/20 transition-smooth min-h-[150px] resize-y"
                  rows={6}
                  data-testid="bio-input"
                />
                <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-2">
                  {formData.bio.length} characters
                </p>
              </div>

              {/* Specialties */}
              <div className="mb-6">
                <label id="specialties-label" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                  Specialties
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(Array.isArray(formData.specialties) ? formData.specialties : []).map((specialty, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 dark:bg-silver/10 border border-light-border dark:border-border rounded-lg font-mono text-sm"
                    >
                      {specialty}
                      <button
                        onClick={() => handleRemoveSpecialty(specialty)}
                        className="text-silver/40 hover:text-red-400 transition-smooth"
                        data-testid={`remove-specialty-${index}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {(!Array.isArray(formData.specialties) || formData.specialties.length === 0) && (
                    <span className="text-slate-400 dark:text-silver/40 font-body text-sm">
                      No specialties added yet
                    </span>
                  )}
                </div>

                {showSpecialtyInput ? (
                  <div className="flex gap-2">
                    <select
                      id="specialty-select"
                      value={newSpecialty}
                      onChange={(e) => setNewSpecialty(e.target.value)}
                      className="flex-1 px-4 py-2 bg-white dark:bg-obsidian border border-light-border dark:border-border rounded-xl font-body text-light-text dark:text-silver focus:outline-none focus:border-slate-400 dark:focus:border-white/20 transition-smooth"
                      aria-labelledby="specialties-label"
                      data-testid="specialty-select"
                    >
                      <option value="">Select a specialty...</option>
                      {SPECIALTY_OPTIONS.filter(s => !(Array.isArray(formData.specialties) ? formData.specialties : []).includes(s)).map(specialty => (
                        <option key={specialty} value={specialty}>{specialty}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleAddSpecialty(newSpecialty)}
                      disabled={!newSpecialty}
                      className="px-4 py-2 bg-slate-100 dark:bg-silver/10 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-silver/20 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowSpecialtyInput(false);
                        setNewSpecialty('');
                      }}
                      className="px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-slate-400 dark:hover:border-white/20 transition-smooth"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSpecialtyInput(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-slate-400 dark:hover:border-white/20 transition-smooth"
                    data-testid="add-specialty-button"
                  >
                    <Plus className="w-4 h-4" />
                    Add Specialty
                  </button>
                )}
              </div>

              {/* Qualifications */}
              <div className="mb-6">
                <label htmlFor="profile-qualifications" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                  Qualifications
                </label>
                <textarea
                  id="profile-qualifications"
                  value={formData.qualifications}
                  onChange={(e) => handleFieldChange('qualifications', e.target.value)}
                  placeholder="List your degrees, certifications, and professional qualifications..."
                  className="w-full px-4 py-3 bg-white dark:bg-obsidian border border-light-border dark:border-border rounded-xl font-body text-light-text dark:text-silver focus:outline-none focus:border-slate-400 dark:focus:border-white/20 transition-smooth min-h-[100px] resize-y"
                  rows={4}
                  data-testid="qualifications-input"
                />
              </div>

              {/* Profile Photo Upload */}
              <div className="mb-6">
                <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                  Profile Photo
                </label>
                <div className="flex items-start gap-6">
                  {/* Photo Preview */}
                  <div className="relative">
                    <div className="w-32 h-32 rounded-xl overflow-hidden bg-slate-100 dark:bg-obsidian border border-light-border dark:border-border flex items-center justify-center">
                      {uploadingPhoto ? (
                        <div className="flex flex-col items-center justify-center">
                          <Loader2 className="w-8 h-8 animate-spin text-silver/60 mb-2" />
                          <span className="font-mono text-xs text-silver/40">Uploading...</span>
                        </div>
                      ) : photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="Photo preview"
                          className="w-full h-full object-cover"
                        />
                      ) : formData.photoUrl ? (
                        <img
                          src={getPhotoUrl(formData.photoUrl)}
                          alt="Profile photo"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      {!uploadingPhoto && !photoPreview && !formData.photoUrl && (
                        <Camera className="w-12 h-12 text-silver/30" />
                      )}
                      {/* Fallback for broken images */}
                      <div className="hidden w-full h-full items-center justify-center">
                        <Camera className="w-12 h-12 text-silver/30" />
                      </div>
                    </div>
                  </div>

                  {/* Upload Controls */}
                  <div className="flex-1">
                    <div className="flex flex-col gap-3">
                      {/* Upload Button */}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handlePhotoSelect}
                          className="hidden"
                          disabled={uploadingPhoto || deletingPhoto}
                          data-testid="photo-upload-input"
                        />
                        <div className={`flex items-center justify-center gap-2 px-4 py-3 border border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-white/20 transition-smooth ${uploadingPhoto || deletingPhoto ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <Upload className="w-4 h-4" />
                          {formData.photoUrl ? 'Change Photo' : 'Upload Photo'}
                        </div>
                      </label>

                      {/* Delete Button (only show if photo exists) */}
                      {formData.photoUrl && (
                        <button
                          onClick={handleDeletePhoto}
                          disabled={uploadingPhoto || deletingPhoto}
                          className="flex items-center justify-center gap-2 px-4 py-3 border border-red-500/30 rounded-xl font-mono text-xs uppercase tracking-widest text-red-400 hover:bg-red-500/10 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="delete-photo-button"
                        >
                          {deletingPhoto ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              Remove Photo
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-3">
                      Accepted formats: JPEG, PNG, GIF, WebP (max 5MB)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                {/* Reset Button */}
                <button
                  onClick={handleReset}
                  disabled={!hasChanges || saving}
                  className="flex items-center gap-2 px-6 py-3 border border-light-border dark:border-border rounded-xl font-mono text-sm uppercase tracking-widest hover:border-slate-400 dark:hover:border-white/20 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reset-button"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>

                {/* Unsaved changes indicator */}
                {hasChanges && (
                  <span className="font-mono text-xs text-yellow-400" data-testid="unsaved-changes-indicator">
                    Unsaved changes
                  </span>
                )}
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="flex items-center gap-2 px-6 py-3 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>

            {/* Changes Summary */}
            {hasChanges && originalData && (
              <div className="mt-6 glass border border-yellow-500/30 rounded-2xl p-4">
                <h3 className="font-mono text-xs uppercase tracking-widest text-yellow-400 mb-3">
                  Pending Changes
                </h3>
                <ul className="space-y-2 font-body text-sm text-light-text-secondary dark:text-silver/60">
                  {formData.bio !== originalData.bio && (
                    <li>• Bio has been modified</li>
                  )}
                  {formData.qualifications !== originalData.qualifications && (
                    <li>• Qualifications have been modified</li>
                  )}
                  {JSON.stringify(formData.specialties) !== JSON.stringify(originalData.specialties) && (
                    <li>• Specialties have been modified</li>
                  )}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DoctorProfileEdit;
